import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Header from "../../common/Header";
import Button from "../../common/Button";
import CustomTextField from "../../common/CustomTextField";
import Card from "../../common/Card";
import {
  AttachmentBarcodeStickerInfo,
  OrderItemInfo,
  OrderInfo,
  TissueTrackingInfo,
  addTissueTracking,
  updateElementInOrderSlice,
  useOrderSlice,
  AttachmentImageOfWoundsInfo,
  AttachmentMedicalNotesInfo,
  fetchOrderDocumentById,
  resetOrdersByPatient,
  fetchOrderDocumentsByIds
} from "../../../redux/slice/order/OrderSlice";
import { useForm } from "react-hook-form";
import {
  AssignmentOutlined,
  AttributionOutlined,
  Cancel,
  ClassOutlined,
  NoteAddOutlined,
  WallpaperOutlined,
} from "@mui/icons-material";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { useLocation } from "react-router-dom";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomDatePicker from "../../common/CustomDatePicker";
import { tokens } from "../../../theme";
import { useEffect, useMemo, useRef, useState } from "react";
import { convertBase64 } from "../../../utils/Utility";
import dayjs from "dayjs";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import React from "react";
import CustomHelpMessage from "../../common/CustomHelpMessage";
import { ORDER_AVAILABILITY, RESPONSE_CODES } from "../../../constants/applicationConstants";
import { useNavigation } from "../../../hooks/useNavigation";
import { getPatient } from "../../../redux/slice/patient/patientSlice";
import { WoundInfo } from "./AncillaryOrderForm";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomIconButton from "../../common/CustomIconButton";
import PDFViewer from "../../common/pdf/PDFViewer";
import CustomItemList from "../../common/CustomItemList";

interface TissueTrackingState extends TissueTrackingInfo {
  checkBoxes: string[],
  selectAll?: boolean
}

export interface OrderItemRow {
  select: JSX.Element;
  orderItemId: number;
  serialNumber: string;
  size: string;
  tissueTrackingId: number;
  productReturnId: number;
  productReturnStatus: string; 
  units: number;
  woundLocation?: string;
  tissueTrackingDocumentId?: number;
  orderNumber?: string;
  orderId?: number;
  productId?: number;
  orderDate?: string;
  productName?: string;
  tissueTrackingInfo?: any;
  productCategory?: string;
  appliedDate?: string;
  returnedDate?: string;
}


const TissueTrackingForMultipleOrders = () => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setError,
    clearErrors,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<TissueTrackingState>();

  const { navigate } = useNavigation();
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { addTissueTrackingStatus, orderDocumentInfo, ordersByPatient } = useOrderSlice();
  const [checkedList, setCheckedList] = useState<number[]>([]);
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });
  const [isAllAppliedOrReturned, setIsAllAppliedOrReturned] = useState(true);
  const [barcodeStickerInitialFiles, setBarcodeStickerInitialFiles] = useState<ExtendedFile[]>([]);
  const [imagesOfWoundsInitialFiles, setImagesOfWoundsInitialFiles] = useState<ExtendedFile[]>([]);
  const [medicalNotesInitialFiles, setMedicalNotesInitialFiles] = useState<ExtendedFile[]>([]);
  const [selectedRow, setSelectedRow] = useState<OrderItemRow[] | []>([]);
  const [showUpdateMFROrderIdPopup, setShowUpdateMFROrderIdPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });
  const [woundInfoRows, setWoundInfoRows] = useState<WoundInfo[]>([]);
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    productName: string;
    fileName: string;
  }>({ isShow: false, productName: "", fileName: "" });
  const [allOrderItems, setAllOrderItems] = useState<OrderItemRow[]>([]);

  const [editableSerialNumber, setEditableSerialNumber] = useState<number | null>(null);
  // Update filters to include ReturnSubmitted and Returned
  const [filters, setFilters] = useState({
    Available: true,
    Applied: false,
    ReturnSubmitted: false,
    ReturnPending: false,
    Returned: false,
  });
  const [filledSerialNumbers, setFilledSerialNumbers] = useState<Record<number, string>>({});
  const triggerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  //Unmount
  useEffect(
    () => () => {
      dispatch(resetOrdersByPatient());
    },
    []
  );

  // CS-746
  useEffect(() => {
    const fetchPatientData = async () => {
      const patientId = location.state.patientId;
      setValue('patientId', patientId);
      if (location.state.orderItems) {
        location.state.orderItems.map((item: { orderItemId: number; serialNumber: string }) => {
          // @ts-ignore
          setValue(`serialNumbers.serialNumber${item.orderItemId}`, item.serialNumber);
          
        });
        setAllOrderItems(location.state.orderItems);
      }      
      if (patientId !== undefined && patientId !== null) {
        const response = await dispatch(getPatient({ patientId: patientId.toString() }));
        // Retrieve all wound details and make options for the dropdown
        if (response.payload && response.payload.code === RESPONSE_CODES.SUCCESS_CODE) {
          const woundInfo = response.payload.data?.patientInfo?.find((info: any) => info.infoType === 'WOUND_INFO' && info.inactiveDate === null);
          let processedWoundData = woundInfo?.patientinfoData?.woundInfo?.woundData?.map((woundData: WoundInfo, i: number) => {
            const totalSize = parseFloat(woundData.totalSize as unknown as string);
            const totalSizeFormatted = isNaN(totalSize) ? '0.00' : totalSize.toFixed(2);
            const cptFormatted = Array.isArray(woundData.cpt) ? woundData.cpt.join(', ') : woundData.cpt;
            return {
              woundType: woundData.woundType,
              cpt: cptFormatted,
              cptIds: woundData.cptIds,
              lCode: woundData.lCode,
              lCodeIds: woundData.lCodeIds,
              eCode: woundData.eCode,
              eCodeIds: woundData.eCodeIds,
              iCode: woundData.iCode,
              iCodeIds: woundData.iCodeIds,
              otherDxCodes: woundData.otherDxCodes,
              noOfApplications: woundData.noOfApplications,
              procedureLocation: woundData.procedureLocation,
              length: woundData.length,
              width: woundData.width,
              depth: woundData.depth,
              totalSize: totalSizeFormatted,
              displayTotalSize: `${totalSizeFormatted}cm² (${woundData.length} X ${woundData.width} X ${woundData.depth})`,
              woundInfoKey: woundData.woundInfoKey
            };
          });
          setWoundInfoRows(processedWoundData || []);
        }
      }
    };

    fetchPatientData();
  }, []);

  // CS-746
  const woundOptions = useMemo(() => {
    let options: { label: string; value: any }[] = [];
    woundInfoRows?.forEach((wound: any) => {
      // const woundNumber = wound.woundInfoKey.split('_').pop() || '1';
      const dimensions = `(${wound.length}cm x ${wound.width}cm x ${wound.depth}cm)`;
      options.push({
        label: `${dimensions} - (${wound.procedureLocation})`,
        value: `${dimensions} - ${wound.procedureLocation} `
      });
    });
    return options;
  }, [woundInfoRows]);

  const handleDropZone = async (uploadedFiles: ExtendedFile[], fieldName: string) => {
    const attachmentBarcodeStickerArray: AttachmentBarcodeStickerInfo[] = [];
    if (uploadedFiles && uploadedFiles.length > 0) {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const base64File = String(await convertBase64(uploadedFiles[i]));
        const fileName = uploadedFiles[i].name;
        const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        const base64Substring = base64File.substring(
          fileExtension === "pdf" ? "data:application/pdf;base64,".length : "data:image/png;base64,".length,
          base64File.length
        );
        const attachmentBarcodeStickerObj: AttachmentBarcodeStickerInfo = {
          documentName: fileName,
          content: base64Substring,
          fileType: fileExtension
        };
        attachmentBarcodeStickerArray.push(attachmentBarcodeStickerObj);
      }
    }
    setValue("attachmentBarcodeSticker", attachmentBarcodeStickerArray);
    trigger("attachmentBarcodeSticker");
  };

  const handleImageOfWounds = async (uploadedFiles: ExtendedFile[], fieldName: string) => {
    const attachmentImageOfWoundsArray: AttachmentImageOfWoundsInfo[] = [];
    if (uploadedFiles && uploadedFiles.length > 0) {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const base64File = String(await convertBase64(uploadedFiles[i]));
        const fileName = uploadedFiles[i].name;
        const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        const base64Substring = base64File.substring(
          fileExtension === "pdf" ? "data:application/pdf;base64,".length : "data:image/png;base64,".length,
          base64File.length
        );
        const attachmentImageOfWoundsObj: AttachmentImageOfWoundsInfo = {
          documentName: fileName,
          content: base64Substring,
          fileType: fileExtension
        };
        attachmentImageOfWoundsArray.push(attachmentImageOfWoundsObj);
      }
    }
    setValue("attachmentImageOfWounds", attachmentImageOfWoundsArray);
    trigger("attachmentImageOfWounds");
  };

  const handleMedicalNotes = async (uploadedFiles: ExtendedFile[], fieldName: string) => {
    const attachmentMedicalNotesArray: AttachmentMedicalNotesInfo[] = [];
    if (uploadedFiles && uploadedFiles.length > 0) {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const base64File = String(await convertBase64(uploadedFiles[i]));
        const fileName = uploadedFiles[i].name;
        const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        const base64Substring = base64File.substring(
          fileExtension === "pdf" ? "data:application/pdf;base64,".length : "data:image/png;base64,".length,
          base64File.length
        );
        const attachmentMedicalNotesObj: AttachmentMedicalNotesInfo = {
          documentName: fileName,
          content: base64Substring,
          fileType: fileExtension
        };
        attachmentMedicalNotesArray.push(attachmentMedicalNotesObj);
      }
    }
    setValue("attachmentMedicalNotes", attachmentMedicalNotesArray);
    trigger("attachmentMedicalNotes");
  };


  const handleDateOfService = (date: Date | null) => {
    if (dayjs(date).isValid()) {
      setValue("dateOfService", dayjs(date).format("MM/DD/YYYY"));
    };
  };

  const onSubmit = () => {
    if (!isAllAppliedOrReturned) {
      if (checkedList?.length > 0) {
        clearErrors("attachmentBarcodeSticker");
        clearErrors("dateOfService");
        clearErrors("checkBoxes");
        clearErrors("selectAll");
        setShowOptionsPopup({
          message: "Do you really want to submit the Record TX? ",
          show: true,
          variant: "confirm",
          buttons: [{ name: "Confirm", color: 'primary', onClick: () => submitTissueTracking() }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
        });
      }
    } else {
      let warningObj: SingleAlertInfo = {
        message: "Order items aren't available to Tissue Track.",
        alertType: "warning"
      };
      dispatch(setSingleAlertObj(warningObj));
    }
  };

  const submitTissueTracking = async () => {
    const { woundLocations, serialNumbers, checkBoxes, selectAll, ...submitObject } = getValues();
    const orderItemList: OrderItemInfo[] = [];

    checkedList?.map(itemId => {
      const selectedItem = selectedRow.find(row => row.orderItemId === itemId);
      const woundLocationValue = ((woundLocations ?? {}) as { [key: string]: string })[`woundLocation${itemId}`];
      const selectedOption = woundOptions.find(option => option.value === woundLocationValue);
      const woundLocationLabel = selectedOption?.label || "";

      orderItemList.push({
        orderItemId: itemId,
        //@ts-ignore
        serialNumber: serialNumbers[`serialNumber${itemId}`],
        //@ts-ignore
        woundLocationValue: woundLocationValue,
        //@ts-ignore
        woundLocationLabel: woundLocationLabel,
        orderId: selectedItem?.orderId,
        productId: selectedItem?.productId
      });
    });

    if (orderItemList?.length > 0) {
      submitObject.orderItems = orderItemList;
    };
    const data = {
      ...submitObject,
      isBarcodeStickerSkipped: getValues("isBarcodeStickerSkipped"),
      manufacturerOrderId: getValues("manufacturerOrderId")
    }
    const response = await dispatch(
      addTissueTracking({ tissueTrackingData: data, interceptorOption: { skipErrorPopup: true } })
    );
    if (response.payload && response.payload.code === RESPONSE_CODES.FAILED_CODE) {
      let alertMessage = response.payload?.message;
      const alertObj: SingleAlertInfo = {
        message: alertMessage,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
    cancelOptionPopup();
  };

  const onError = (_errors: any, e: any) => {
    const { attachmentBarcodeSticker, dateOfService } = getValues();
    let hasError = false;

    if (checkedList?.length <= 0) {
      let errorObj: SingleAlertInfo = {
        message: "Please select at least one Order item.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      hasError = true;
      cancelOptionPopup();
      // clearErrors("attachmentBarcodeSticker");
    } else if (dateOfService === undefined || dateOfService === "" || dateOfService === null) {
      let errorObj: SingleAlertInfo = {
        message: "Date of Service is Required.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      hasError = true;
      cancelOptionPopup();
      clearErrors("checkBoxes");
      clearErrors("selectAll");

    } else if ((selectedRow.length === 0 || hasSerialNumberPresent(selectedRow)) && attachmentBarcodeSticker?.length <= 0) {
      let errorObj: SingleAlertInfo = {
        message: "Barcode Sticker Attachment is required.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      hasError = true;
      cancelOptionPopup();
      clearErrors("checkBoxes");
      clearErrors("selectAll");
    } else {
      const serialNumbers = getValues("serialNumbers");
      const woundLocations: any = getValues("woundLocations");
      let isWoundLocationValid = true;
      let isNotEmpty = false;
      const hasBarcodeSticker = getValues("attachmentBarcodeSticker").length > 0;

      checkedList?.map(itemId => {
        //@ts-ignore
        if (serialNumbers[`serialNumber${itemId}`]) {
          isNotEmpty = true;
        }
        //@ts-ignore
        else if (!serialNumbers[`serialNumber${itemId}`] && hasBarcodeSticker) {
          isNotEmpty = true;
        }
        if (!woundLocations?.[`woundLocation${itemId}`]) {
          isWoundLocationValid = false;
        }
      });

      if (isNotEmpty) {
        clearErrors("attachmentBarcodeSticker");
        clearErrors("checkBoxes");
        clearErrors("selectAll");
      }

      if (!isWoundLocationValid) {
        let errorObj: SingleAlertInfo = {
          message: "Please select a Wound Location for all selected Order items.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errorObj));
        hasError = true;
        cancelOptionPopup();
        clearErrors("checkBoxes");
        clearErrors("selectAll");
      }
    }
    if (Object.keys(errors).length > 0 || hasError) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      onSubmit();
    }
  };

  useEffect(() => {
    if (addTissueTrackingStatus === 'success') {
      let successObj: SingleAlertInfo = {
        message: `Record Product Usage for Orders has been submitted successfully.`,
        alertType: "success"
      };
      dispatch(setSingleAlertObj(successObj));
      dispatch(updateElementInOrderSlice(
        {
          elementName: "addTissueTrackingStatus",
          value: "idle"
        }
      ));
      cancelOptionPopup();
      navigate(-1);
    }
  }, [addTissueTrackingStatus])

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  useEffect(() => {
    if (allOrderItems) {
      const isAllItemAppliedOrReturned = allOrderItems.every((item: { productReturnId: any; tissueTrackingId: any; }) => item.productReturnId || item.tissueTrackingId);
      setIsAllAppliedOrReturned(!!isAllItemAppliedOrReturned);
    };
  }, [allOrderItems]);

  const handleCheckBox = (e: React.ChangeEvent<HTMLInputElement>, row: OrderItemRow) => {
    const { checked } = e.target;
    if (checked) {
      row && setCheckedList(prevValues => {
        const newValues: number[] = [...prevValues];
        newValues.push(row.orderItemId);
        return newValues;
      });

      setSelectedRow((data) => {
        let selectedOrderItemRow: OrderItemRow[] = [];

        data?.map((itemRow) => selectedOrderItemRow.push(itemRow));
        selectedOrderItemRow.push(row);
        return selectedOrderItemRow;
      })
        setCheckedList((prevValues) => [...prevValues, row.orderItemId]);
        setSelectedRow((data) => [...data, row]);
      } else {
      setCheckedList(checkedList.filter(item => item !== row.orderItemId));

      setSelectedRow((data) => {
        return data?.filter((itemRow) => itemRow.orderItemId !== row.orderItemId) || [];
      })
      setValue("selectAll", false);

      setCheckedList((prevValues) => prevValues.filter((id) => id !== row.orderItemId));
      setSelectedRow((data) => data.filter((item) => item.orderItemId !== row.orderItemId));
      if (editableSerialNumber === row.orderItemId) {
        setEditableSerialNumber(null);
      }
    }
  };


  // CS-746
  const handleLocationChange = (row: OrderItemRow, value: any) => {
    if (value) {
      const selectedOption = woundOptions.find(option => option.value === value);
      const label = selectedOption?.label || "";

      let existingRow = selectedRow.find(item => item.orderItemId === row.orderItemId);

      if (existingRow) {
        let updatedRow: OrderItemRow = {
          ...existingRow,
          woundLocation: value,
        };

        setSelectedRow((prevRows) => {
          let otherRows = prevRows?.filter((item) => item.orderItemId !== row.orderItemId) || [];
          return [...otherRows, updatedRow];
        });
      }
    }
  };

  const checkSerialNumberPresent = (row: OrderItemRow) => {
    if (checkedList.includes(row.orderItemId)) {
      return false;
    }
    return true;
  };

  const hasSerialNumberPresent = (rows: OrderItemRow[]) => {
    return rows?.some((element: OrderItemRow) => {
      const formSerialNumber = filledSerialNumbers[element.orderItemId] || element.serialNumber;
      return !formSerialNumber;
    });
  };

  //CS-1164 START
  const isCheckBoxDisable = (row: OrderItemRow) => {
    if (row?.tissueTrackingId) {
      return true;
    }
    if (row?.productReturnId && row?.productReturnStatus !== ORDER_AVAILABILITY.RETURN_REJECTED) {
      return true;
    }
    return false;
  };

  const orderItemStatus = (row: OrderItemRow) => {
    if (row?.tissueTrackingId) {
      return "Applied";
    } else if (row?.productReturnId) {
      // return `Returned`;
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_INITIATED) {
        return "Return Initiated";
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING) {
        return "Return Pending";
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURNED) {
        return "Returned";
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) {
        return "Available(Return Rejected)";
      }
    }
    return "Available";
  };
  //CS-1164 END

  const handleSelectAllCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (checked) {
      const selectList: number[] = [];
      const newSelectedRows: OrderItemRow[] = [...selectedRow];
      const checkBoxesObj: Record<string, boolean> = {};

      allOrderItems?.forEach(row => {
        if (!isCheckBoxDisable(row)) {
          selectList.push(row.orderItemId);
          checkBoxesObj[`checkbox${row.orderItemId}`] = true;

          if (!newSelectedRows.some(r => r.orderItemId === row.orderItemId)) {
            newSelectedRows.push(row);
          }
        }
      });

      setCheckedList(selectList);
      setSelectedRow(newSelectedRows);
      //@ts-ignore
      setValue('checkBoxes', checkBoxesObj);
    } else {
      const checkBoxesObj: Record<string, boolean> = {};
      allOrderItems?.forEach(row => {
        if (!isCheckBoxDisable(row)) {
          checkBoxesObj[`checkbox${row.orderItemId}`] = false;
        }
      });

      setCheckedList([]);
      setSelectedRow(prev =>
        prev.filter(itemRow =>
          isCheckBoxDisable(
            allOrderItems.find(r => r.orderItemId === itemRow.orderItemId)!
          )
        )
      );
      //@ts-ignore
      setValue('checkBoxes', checkBoxesObj);
    }
  };

  const handlePreviewAction = async (row: OrderItemRow) => {
    let documentId = row.tissueTrackingDocumentId
      ? row.tissueTrackingDocumentId
      : 0;
    const actionResult = await dispatch(fetchOrderDocumentById(documentId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName:
          row?.productName +
          (orderItemStatus(row) === `Returned (#${row.productReturnId})`
            ? ` - RETURN`
            : ` - TISSUE TRACKING`),
        fileName:
          orderItemStatus(row) === `Returned (#${row.productReturnId})`
            ? `RETURN_${row?.orderNumber}`
            : `TISSUE_TRACKING_${row?.orderNumber}`,
      });
    }
  };

  const handleImageViewAction = async (row: OrderItemRow) => {
    let documentIds = row.tissueTrackingInfo?.imagesOfWounds ?? [];
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row?.productName + ` - TISSUE TRACKING`,
        fileName: `TISSUE_TRACKING_${row?.orderNumber}`,
      });
    }
  };

  const handleViewMedicalNotesAction = async (row: OrderItemRow) => {
    let documentIds = row.tissueTrackingInfo?.medicalNotes ?? [];
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row?.productName + " - TISSUE TRACKING",
        fileName: `TISSUE_TRACKING_MEDICAL_NOTES_${row?.orderNumber}`,
      });
    }
  };

    const columns = [
      {
        id: "select",
        label: isMobile ? (
          <Box />
        ) : (
          filters.Available ? (
            <CustomCheckbox
              control={control}
              controllerName="selectAll"
              onChange={handleSelectAllCheckbox}
              tooltipMessage="Select All"
              tooltipPlacement="top"
              rules={!isAllAppliedOrReturned ? { required: true } : {}}
            />
          ) : (
            <Box />
          )
        ),
        minWidth: 50,
      },
      { id: "productName", label: "Product Name", minWidth: 200  },
      { id: "size", label: "Size", minWidth: 100 },
      { id: "orderID", label: "OrderID", minWidth: 130 },
      { id: "date", label: "Order Date", minWidth: 100 },
      { id: "serialNumber", label: "Serial Number",minWidth: 180 },
      { id: "status", label: "Status", minWidth: 130 },
      { id: "woundLocation", label: "Wound Location", minWidth: 180 },
      { id: "document", label: "Document",minWidth: 100 },
      { id: "appRet", label: "App/Ret", minWidth: 100}
    ];
    

    const handleSerialNumberClick = (row: OrderItemRow) => {
      if (checkedList.includes(row.orderItemId)) {
        setEditableSerialNumber(row.orderItemId);
      }
    };

  const handleSerialNumberBlur = async (orderItemId?: number) => {
    if (orderItemId !== undefined) {
      const value = getValues(`serialNumbers.serialNumber${orderItemId}` as any);
      setFilledSerialNumbers(prev => ({ ...prev, [orderItemId]: value || '' }));

      const isValid = await trigger(`serialNumbers.serialNumber${orderItemId}` as any);
      if (isValid) {
        setEditableSerialNumber(null);
      }
    }
  };

    const filteredOrderItems = allOrderItems.filter((item) => {
      const status = orderItemStatus(item);

      return (
        (filters.Available && (status === "Available" || status === "Available(Return Rejected)")) ||
        (filters.Applied && status === "Applied") ||
        (filters.ReturnSubmitted && status === "Return Initiated") ||
        (filters.ReturnPending && status === "Return Pending") ||
        (filters.Returned && status === "Returned")
      );
    });


  const rows = useMemo(() => filteredOrderItems.map((row) => ({
    select: (
      <CustomCheckbox
        isDisabled={isCheckBoxDisable(row)}
        onChange={(e) => handleCheckBox(e, row)}
        control={control}
        controllerName={`checkBoxes.checkbox${row.orderItemId}`}
        rules={!isAllAppliedOrReturned ? { required: true } : {}}
      />
    ),
    productName: row.productName,
    size: row.size,
    orderID: row.orderNumber,
    date: row.orderDate,
    // orderInfo: `${row.orderNumber} - ${row.orderDate}`,
    serialNumber: (
      checkedList.includes(row.orderItemId) ? (
        editableSerialNumber === row.orderItemId ? (
          <CustomTextField
            control={control}
            isDisabled={false}
            controllerName={`serialNumbers.serialNumber${row.orderItemId}`}
            label="Serial Number"
            sx={{ width: "100%" }}
            onBlur={() => handleSerialNumberBlur(row.orderItemId)}
            onChange={() => {
              if (triggerDebounceRef.current) clearTimeout(triggerDebounceRef.current);
              triggerDebounceRef.current = setTimeout(() => {
                trigger(`serialNumbers.serialNumber${row.orderItemId}` as any);
              }, 400);
            }}
            rules={{ maxLength: { value: 50, message: "Serial Number cannot exceed 50 characters" } }}
            autoFocus
          />
        ) : (

          <Typography
            onClick={() => handleSerialNumberClick(row)}
            sx={{
              cursor: checkedList.includes(row.orderItemId) ? "pointer" : "not-allowed",
              color: checkedList.includes(row.orderItemId) ? "white" : "gray",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {/* @ts-ignore */}
            {getValues(`serialNumbers.serialNumber${row.orderItemId}`) || "Click to Edit"}
          </Typography>

        )
      ) : (
        <Typography
          sx={{
            cursor: "not-allowed",
            color: "#B0B0B0",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.serialNumber || "Not Available"}
        </Typography>
      )
    ),
    status: orderItemStatus(row),
    woundLocation: !isCheckBoxDisable(row) && checkedList.includes(row.orderItemId) ? (
      <CustomDropdownField
        label="Select Wound"
        name={`woundLocations.woundLocation${row.orderItemId}`}
        options={woundOptions}
        control={control}
        onChangeSelect={(e, v) => handleLocationChange(row, v)}
        rules={{ required: "Select Wound is required." }}
      />
    ) : (
      <Typography>{row.woundLocation}</Typography>
    ),
    document: (
      <Grid container spacing={1} alignItems="center">
        {row.tissueTrackingDocumentId && (
          <CustomIconButton
            tooltipPlacement="top"
            tooltipMessage="Document"
            color="info"
            size="small"
            variant="outlined"
            onClick={() => handlePreviewAction(row)}
            sx={{ mr: 1 }}
          >
            <AssignmentOutlined fontSize="small" />
          </CustomIconButton>
        )}
        {row.tissueTrackingInfo?.imagesOfWounds?.length > 0 && (
          <CustomIconButton
            tooltipPlacement="top"
            tooltipMessage="Image of Wound"
            color="info"
            size="small"
            variant="outlined"
            onClick={() => handleImageViewAction(row)}
            sx={{ mr: 1 }}
          >
            <WallpaperOutlined fontSize="small" />
          </CustomIconButton>
        )}
        {row.tissueTrackingInfo?.medicalNotes?.length > 0 && (
          <CustomIconButton
            tooltipPlacement="top"
            tooltipMessage="Medical Notes"
            color="info"
            size="small"
            variant="outlined"
            onClick={() => handleViewMedicalNotesAction(row)}
          >
            <NoteAddOutlined fontSize="small" />
          </CustomIconButton>
        )}
      </Grid>
    ),
    appRet: (() => {
      const applied = row?.appliedDate;
      const returned = row?.returnedDate;
      if (row?.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) return "";

      if (applied) return applied;
      if (returned) return returned;
      return "";
    })(),
  })), [filteredOrderItems, checkedList, editableSerialNumber, woundOptions, isAllAppliedOrReturned]);

  return (
    <Box
      sx={{
        margin: {
          xs: "20px 10px",
          sm: "20px",
        },
      }}
    >

      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, productName: "", fileName: "" })
          }
          title={showPDF.productName}
          fileName={showPDF.fileName}
          base64String={orderDocumentInfo?.content || ""}
        />
      )}

      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}
      {showUpdateMFROrderIdPopup.show && (
        <OptionsPopup
          open={showUpdateMFROrderIdPopup.show}
          onClose={() => setShowUpdateMFROrderIdPopup({ message: "", show: false })}
          variant={showUpdateMFROrderIdPopup.variant}
          message={showUpdateMFROrderIdPopup.message}
          buttons={showUpdateMFROrderIdPopup.buttons}
          title={showUpdateMFROrderIdPopup.title}
          obj={showUpdateMFROrderIdPopup.obj}
        />
      )}
      <Box mb={2}>
        <Grid container spacing={2} display="flex" alignItems="center">
          <Grid item>
            <Header
              title="Record Product Usage"
              subtitle=""
              message="The procedure was a success! Now let's document the procedure and the products used, applied, or implanted. To do so, please fill the below fields by following the prompts below."
            />
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Grid container spacing={3}>
          <Grid item md={12} xs={12} sx={{ order: { xs: 2, md: 1 } }}>
            <Card sx={{ padding: 0 }}>
              <form onSubmit={handleSubmit(onSubmit, onError)}>
                <br></br>
                <Box display="flex" alignItems="center" gap={1} mb={2} ml={2}>
                  <AttributionOutlined sx={{ color: "#bbb" }} />
                  <Typography sx={{ color: colors.success[300], fontWeight: "bold" }}>Patient: </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                    {location.state.patientName}
                  </Typography>
                </Box>
                <Divider />
                {filters.Available && (
                <Grid container spacing={2} pl={2} mt={2} pr={2} display="flex" justifyContent="flex-start" alignItems="center">
                  <Grid item xs={12} md={"auto"}>
                    <Typography fontWeight="bold">
                      Date of Service (DOS){" "}
                      <CustomHelpMessage
                        title="Help"
                        message="Select the date the product was applied to the patient."
                      />
                    </Typography>
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <CustomDatePicker
                      control={control}
                      onChange={handleDateOfService}
                      controllerName="dateOfService"
                      label="Date Of Service"
                      rules={
                        !isAllAppliedOrReturned
                          ? { required: "Date of Service is Required." }
                          : {}
                      }
                    />
                  </Grid>
                </Grid>
                )}
                <Grid container spacing={2} pl={2} pr={2} mt={0.2}>
                  <Grid item md={12} mb={2}>
                    <Typography fontWeight="bold">
                      Select Applied Products{" "}
                      <CustomHelpMessage
                        title="Help"
                        message="Check the box of each product item that was utilized or applied to this patient on the above Date of Service. Ensure the Serial# aligns with the Tissue ID# on the barcode sticker found in the product's original packaging."
                      />
                    </Typography>
                    {/*  CS-746 */}
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ p:1, borderRadius : 3, backgroundColor : colors.grey[900]}}>
                          <Box display="flex" gap={2} alignItems="center" px={2} sx={{ borderBottom: 1, borderRadius: 2, mb: 2, borderColor: colors.grey[700], flexWrap: 'wrap', rowGap: 1 }}>
                            {["Available", "Applied", "ReturnSubmitted", "ReturnPending", "Returned"].map((label) => (
                              <CustomCheckbox
                                key={label}
                                controllerName={`filterCheck.${label}`}
                                control={control}
                                label={label === "ReturnSubmitted" ? "Return Initiated" : label === "ReturnPending" ? "Return Pending" : label}
                                checked={filters[label as keyof typeof filters]}
                                onChange={(e) => {
                                  const { checked } = e.target;
                                  setFilters((prev) => ({
                                    ...prev,
                                    [label]: checked
                                  }));
                                }}
                              />
                            ))}
                          </Box>
                          <CustomItemList columns={columns} rows={rows} maxHeight= {400}/>
                        </Box>
                        {/* <Box
                          sx={{
                            maxHeight: 400,
                            overflowY: 'auto',
                          }}
                        >
                        <List sx={{ bgcolor: colors.grey[900], borderRadius: 3 }}>
                          <ListItem>
                            <Grid container alignItems="center">
                              <Grid item md={0.5} xs={0.5}>
                                <CustomCheckbox
                                  control={control}
                                  controllerName="selectAll"
                                  onChange={handleSelectAllCheckbox}
                                  tooltipMessage="Select All"
                                  tooltipPlacement="top"
                                  rules={!isAllAppliedOrReturned ? { required: true } : {}}
                                />
                              </Grid>
                              <Grid item md={1.5} xs={2} sx={{ marginLeft: { xs: '15px', md: '0px' } }}>
                                <Typography><b>Product Name</b></Typography>
                              </Grid>
                              <Grid item md={1} xs={2} sx={{ marginLeft: { xs: '20px', md: '0px' } }}>
                                <Typography><b>Size</b></Typography>
                              </Grid>
                              <Grid item md={2} xs={2} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                <Typography><b>OrderID & Date</b></Typography>
                              </Grid>
                              <Grid item md={2.5} xs={6} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                <Typography><b>Serial Number</b></Typography>
                              </Grid>
                              <Grid item md={0.5} xs={0}>
                              </Grid>
                              <Grid item md={1} xs={3} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                <Typography><b>Status</b></Typography>
                              </Grid>
                              <Grid item md={2} xs={4} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                <Typography><b>Wound - Location</b></Typography>
                              </Grid>
                              <Grid item md={1} xs={3}>
                                <Typography><b>Document</b></Typography>
                              </Grid>
                            </Grid>
                          </ListItem>
                          {allOrderItems.length === 0 ? (
                            <ListItem>
                              <Grid container justifyContent="center">
                                <Typography color="text.secondary">No order items found.</Typography>
                              </Grid>
                            </ListItem>
                          ) : (
                          allOrderItems.map((row) => (
                            <ListItem key={row.orderItemId}>
                              <Grid container alignItems="center" spacing={1}>
                                <Grid item md={0.5} xs={0.5}>
                                  <CustomCheckbox
                                    isDisabled={isCheckBoxDisable(row)}
                                    onChange={(e) => handleCheckBox(e, row)}
                                    control={control}
                                    controllerName={`checkBoxes.checkbox${row.orderItemId}`}
                                    rules={!isAllAppliedOrReturned ? { required: true } : {}}
                                  />
                                </Grid>
                                <Grid item md={1.5} xs={2} sx={{ marginLeft: { xs: '12px', md: '0px' } }}>
                                  <Typography>{row.productName}</Typography>
                                </Grid>
                                <Grid item md={1} xs={2} sx={{ marginLeft: { xs: '20px', md: '0px' } }}>
                                  <Typography>{row.size}</Typography>
                                </Grid>
                                <Grid item md={2} xs={2} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                  <Typography>{row.orderNumber + " - " + row.orderDate}</Typography>
                                </Grid>
                                <Grid item md={2.5} xs={6} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                  <CustomTextField
                                    control={control}
                                    isDisabled={checkSerialNumberPresent(row)}
                                    controllerName={`serialNumbers.serialNumber${row.orderItemId}`}
                                    label="Serial Number"
                                    sx={{ width: "100%" }}
                                    onChange={(e) => handleSerialNumberChange(e, row)}
                                  />
                                </Grid>
                                <Grid item md={0.5} xs={0}>
                                </Grid>
                                <Grid item md={1} xs={3} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                  <Typography>{orderItemStatus(row)}</Typography>
                                </Grid>
                                <Grid item md={2} xs={4} sx={{ marginLeft: { xs: '10px', md: '0px' } }}>
                                  {!isCheckBoxDisable(row) && checkedList.includes(row.orderItemId) && (
                                    <CustomDropdownField
                                      label="Select Wound"
                                      name={`woundLocations.woundLocation${row.orderItemId}`}
                                      options={woundOptions}
                                      control={control}
                                      onChangeSelect={(e, v) => handleLocationChange(row, v)}
                                      rules={{ required: "Select Wound is required." }}
                                    />
                                  )}
                                  {row.tissueTrackingId && <Typography>{row.woundLocation}</Typography>}
                                </Grid>
                                <Grid item md={1} xs={3}>
                                  {row.tissueTrackingDocumentId && (
                                    <CustomIconButton
                                      tooltipPlacement="top"
                                      tooltipMessage="Document"
                                      color="info"
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handlePreviewAction(row)}
                                      sx={{ mr: 1 }}
                                    >
                                      <AssignmentOutlined fontSize="small" />
                                    </CustomIconButton>
                                  )}
                                  {row.tissueTrackingInfo?.imagesOfWounds?.length > 0 && (
                                    <CustomIconButton
                                      tooltipPlacement="top"
                                      tooltipMessage="Image of Wound"
                                      color="info"
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleImageViewAction(row)}
                                      sx={{ mr: 1 }}
                                    >
                                      <WallpaperOutlined fontSize="small" />
                                    </CustomIconButton>
                                  )}
                                  {row.tissueTrackingInfo?.medicalNotes?.length > 0 && (
                                    <CustomIconButton
                                      tooltipPlacement="top"
                                      tooltipMessage="Medical Notes"
                                      color="info"
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleViewMedicalNotesAction(row)}
                                    >
                                      <NoteAddOutlined fontSize="small" />
                                    </CustomIconButton>
                                  )}
                                </Grid>
                              </Grid>
                            </ListItem>
                          )))}
                        </List>
                        </Box> */}
                      </Grid>
                    </Grid>
                    {/*  */}
                  </Grid>
                </Grid>
                {filters.Available && (
                  <>
                    <Grid container spacing={2} pl={2} pr={2}>
                      <Grid md={4} xs={12} item>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="h6" fontWeight="bold">
                              Upload Image of Barcode Sticker(s)
                            </Typography>
                            {!(selectedRow.length === 0 || !hasSerialNumberPresent(selectedRow)) && (
                              <CustomHelpMessage
                                title="Help"
                                message="Take a photo or upload an image of the barcode sticker(s)."
                              />
                            )}
                          </Box>
                          <Typography variant="h6" fontWeight="bold" sx={{ minHeight: "24px" }}>
                            {(selectedRow.length === 0 || !hasSerialNumberPresent(selectedRow)) && (
                              <>
                                [Optional]{" "}
                                <CustomHelpMessage
                                  title="Help"
                                  message="Take a photo or upload an image of the barcode sticker(s)."
                                />
                              </>
                            )}
                          </Typography>
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <Dropzone
                            onFileChangeTest={handleDropZone}
                            name="attachmentBarcodeSticker"
                            onFileChange={() => { }}
                            control={control}
                            subLabel={{
                              textColor: colors.error[300],
                              text:
                                (selectedRow.length === 0 ||
                                  hasSerialNumberPresent(selectedRow)) &&
                                  getValues("attachmentBarcodeSticker")?.length <=
                                  0 ? (
                                  <></>
                                ) : (
                                  <span
                                    style={{
                                      fontStyle: "italic",
                                      fontSize: "14px",
                                    }}
                                  >
                                    (Not Required)
                                  </span>
                                ),
                            }}
                            rules={
                              selectedRow.length === 0 ||
                                !hasSerialNumberPresent(selectedRow)
                                ? undefined
                                : {
                                  required:
                                    "Barcode Sticker Attachment is required.",
                                }
                            }
                            initialFiles={barcodeStickerInitialFiles}
                            holdOnChange={true}
                            isDisabled={getValues("isBarcodeStickerSkipped")}
                          />
                        </Grid>
                      </Grid>
                      <Grid md={4} xs={12} item>
                        <Grid item xs={12}>
                          <Typography variant="h6" fontWeight="bold">
                            Upload Image of Wound
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            [Optional]{" "}
                            <CustomHelpMessage
                              title="Help"
                              message="Upload images of wound. This will bolster your protection during any audit."
                            />
                          </Typography>
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <Dropzone
                            onFileChangeTest={handleImageOfWounds}
                            name="attachmentImageOfWounds"
                            onFileChange={() => { }}
                            control={control}
                            // rules={{ required: "Image of wounds are required." }}
                            initialFiles={imagesOfWoundsInitialFiles}
                            holdOnChange={true}
                          />
                        </Grid>
                      </Grid>
                      <Grid md={4} xs={12} item>
                        <Grid item xs={12}>
                          <Typography variant="h6" fontWeight="bold">
                            Upload Medical Notes
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            [Optional]{" "}
                            <CustomHelpMessage
                              title="Help"
                              message="Upload Medical Notes. This will bolster your protection during any audit."
                            />
                          </Typography>
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <Dropzone
                            onFileChangeTest={handleMedicalNotes}
                            name="attachmentMedicalNotes"
                            onFileChange={() => { }}
                            control={control}
                            initialFiles={medicalNotesInitialFiles}
                            holdOnChange={true}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} pl={2} pr={2} mt={1}>
                      <Grid item md={12} xs={12}>
                        <CustomTextField
                          controllerName="comment"
                          control={control}
                          label="Any Additional Comments for this Tissue Utilization Record? List them here."
                          multiline
                          rows={4}
                          maxRows={4}
                        />
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      spacing={2}
                      display="flex"
                      justifyContent="end"
                      mb={5}
                      p={2}
                    >
                      <Grid item md={"auto"} xs={12}>
                        <Button
                          type="submit"
                          color="success"
                          startIcon={<ClassOutlined />}
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: "100%", md: "auto" },
                          }}
                        >
                          Submit
                        </Button>
                      </Grid>
                      <Grid item md={"auto"} xs={12}>
                        <Button
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => navigate(-1)}
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: "100%", md: "auto" },
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </>
                )}
              </form>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TissueTrackingForMultipleOrders;
